import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Container from '../common/Container';
import Card from '../common/Card';
import Button from '../common/Button';
import Text from '../common/Text';
import { ArrowRightIcon, ClockIcon, QuestionMarkCircleIcon, ChartBarIcon } from '@heroicons/react/24/outline';

const QuickAssessment: React.FC = () => {
  const { tenant } = useParams<{ tenant: string }>();
  const navigate = useNavigate();
  return (
    <Container>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <Text variant="h1" className="sm:text-3xl">
            Quick Assessment
          </Text>
          <Text variant="p" className="mt-2">
            Get started with your skill assessment and receive personalized recommendations.
          </Text>
        </div>

        {/* Assessment Overview */}
        <Card className="overflow-hidden border border-gray-200 dark:border-gray-700">
          <div className="p-6 space-y-6">
            <div className="space-y-2">
              <Text variant="h3">
                About This Assessment
              </Text>
              <Text variant="p">
                A focused assessment designed to quickly evaluate your current knowledge level and provide targeted recommendations.
              </Text>
            </div>

            {/* Assessment Details */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start space-x-3">
                <ClockIcon className="w-5 h-5 text-indigo-500 dark:text-indigo-400 mt-0.5" />
                <div>
                  <Text variant="h4">Duration</Text>
                  <Text variant="p" className="text-sm">15-20 minutes</Text>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <QuestionMarkCircleIcon className="w-5 h-5 text-indigo-500 dark:text-indigo-400 mt-0.5" />
                <div>
                  <Text variant="h4">Questions</Text>
                  <Text variant="p" className="text-sm">20 questions</Text>
                </div>
              </div>
            </div>

            {/* What to Expect */}
            <div>
              <Text variant="h4" className="mb-3">What to expect:</Text>
              <ul className="space-y-2.5">
                <li className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-green-500 dark:text-green-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <Text variant="span">Multiple choice questions covering key concepts</Text>
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-green-500 dark:text-green-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <Text variant="span">Adaptive difficulty based on your responses</Text>
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-green-500 dark:text-green-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <Text variant="span">Detailed skill report with strengths and areas for improvement</Text>
                </li>
                <li className="flex items-center">
                  <svg className="w-4 h-4 mr-2 text-green-500 dark:text-green-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <Text variant="span">Personalized study recommendations</Text>
                </li>
              </ul>
            </div>

            {/* Instructions */}
            <div>
              <Text variant="h4" className="mb-3">Before you begin:</Text>
              <ul className="space-y-2">
                <li>
                  <Text variant="span">• Ensure you have a stable internet connection</Text>
                </li>
                <li>
                  <Text variant="span">• Find a quiet place where you can focus</Text>
                </li>
                <li>
                  <Text variant="span">• You can pause the assessment if needed</Text>
                </li>
                <li>
                  <Text variant="span">• Answer each question to the best of your ability</Text>
                </li>
              </ul>
            </div>

            <Button
              onClick={() => navigate(`/${tenant}/skill-diagnostic/start/quick`)}
              variant="primary"
              size="lg"
              rightIcon={<ArrowRightIcon className="w-5 h-5" />}
              className="w-full sm:w-auto"
            >
              Begin Assessment
            </Button>
          </div>
        </Card>
      </div>
    </Container>
  );
};

export default QuickAssessment;